/**
 * The cellar document's versions (CLAUDE.md data safety rule 3). Every read goes through
 * `migrateCellar`, which brings an older document up to date and refuses one newer than this
 * app knows, so a version is never written back over data the app does not understand.
 *
 * - 1: the first shape (data-model.md).
 * - 2 (owner request, 2026-10-01): a purchase's place (`placeKind`) is optional. Every version 1
 *   document is already valid version 2 data, so the migration only records the version.
 * - 3 (owner request, 2026-10-03): a wine may have a `region`. Version 2 data is valid version 3
 *   data; the migration only records the version.
 * - 4 (owner request, 2026-10-03): a wine may have a saved `onlineRating` (domain/online-rating.ts).
 *   Version 3 data is valid version 4 data; the migration only records the version.
 * - 5 (owner request, 2026-10-03): a saved online rating may also hold the day of the lookup and
 *   each site's own rating, for the wine's notes. Version 4 data is valid version 5 data; the
 *   migration only records the version.
 * - 6 (owner requests, 2026-10-06): a purchase's place (`placeKind`, `placeDetail`) becomes a
 *   free-text `seller` (the detail, else the kind's name: "Caveau", "Domaine", "Grossiste"); a
 *   purchase may keep its `purchasedDay`; the drink-by month (`drinkUntilMonth`) is removed, the
 *   due date becoming the end of the drink-by year. Destructive: the PR has the backup note.
 * - 7 (owner requests, 2026-10-09): a tasting may take several bottles (`bottles`, absent meaning
 *   one), and the purchase's `seller` is removed from the app and erased. Destructive (the
 *   sellers are dropped): the PR has the backup and rollback note.
 * - 8 (owner request, 2026-10-11): a wine may name its label photos (`labelPhotos`, front and
 *   back; domain/label-photos.ts). Version 7 data is valid version 8 data; the migration only
 *   records the version.
 */
import type { Cellar } from './cellar';

export const CELLAR_SCHEMA_VERSION = 8;

export class CellarVersionTooNewError extends Error {
  constructor(public readonly schemaVersion: number) {
    super(`cellar.json version ${schemaVersion} is newer than this app understands.`);
    this.name = 'CellarVersionTooNewError';
  }
}

/** Version 1 to 2: nothing to change in the data (the place only became optional). */
export function migrateCellarV1ToV2(v1: Cellar): Cellar {
  return { ...v1, schemaVersion: 2 };
}

/** Version 2 to 3: nothing to change in the data (the region is new and optional). */
export function migrateCellarV2ToV3(v2: Cellar): Cellar {
  return { ...v2, schemaVersion: 3 };
}

/** Version 3 to 4: nothing to change in the data (the saved online rating is new and optional). */
export function migrateCellarV3ToV4(v3: Cellar): Cellar {
  return { ...v3, schemaVersion: 4 };
}

/** Version 4 to 5: nothing to change in the data (the online rating's details are optional). */
export function migrateCellarV4ToV5(v4: Cellar): Cellar {
  return { ...v4, schemaVersion: 5 };
}

/** The French names of the former place kinds, the seller of a purchase that had no detail. */
const PLACE_KIND_NAMES: Record<string, string> = {
  caveau: 'Caveau',
  domaine: 'Domaine',
  grossiste: 'Grossiste',
};

/**
 * Version 5 to 6 (owner, 2026-10-06): each purchase's place becomes its seller (the detail, else
 * the kind's name); the drink-by month is removed. Nothing else changes.
 */
export function migrateCellarV5ToV6(v5: Cellar): Cellar {
  return {
    ...v5,
    schemaVersion: 6,
    wines: v5.wines.map((stored) => {
      const { drinkUntilMonth: _month, ...wine } = stored as typeof stored & {
        drinkUntilMonth?: number;
      };
      return {
        ...wine,
        purchases: wine.purchases.map((stored) => {
          const { placeKind, placeDetail, ...purchase } = stored as typeof stored & {
            placeKind?: string;
            placeDetail?: string;
          };
          const seller =
            placeDetail?.trim() || (placeKind ? PLACE_KIND_NAMES[placeKind] : undefined);
          return { ...purchase, ...(seller ? { seller } : {}) };
        }),
      };
    }),
  };
}

/**
 * Version 6 to 7 (owner, 2026-10-09): every purchase's seller is erased. A tasting's bottle count
 * is new and optional, so tastings stay as they are (one bottle each).
 */
export function migrateCellarV6ToV7(v6: Cellar): Cellar {
  return {
    ...v6,
    schemaVersion: 7,
    wines: v6.wines.map((wine) => ({
      ...wine,
      purchases: wine.purchases.map((stored) => {
        const { seller: _seller, ...purchase } = stored as typeof stored & { seller?: string };
        return purchase;
      }),
    })),
  };
}

/** Version 7 to 8: nothing to change in the data (the label photos are new and optional). */
export function migrateCellarV7ToV8(v7: Cellar): Cellar {
  return { ...v7, schemaVersion: 8 };
}

/** A stored document as the current version, or an error if it cannot be read safely. */
export function migrateCellar(stored: unknown): Cellar {
  const doc = stored as { schemaVersion?: unknown; wines?: unknown } | null;
  if (
    typeof doc !== 'object' ||
    doc === null ||
    typeof doc.schemaVersion !== 'number' ||
    !Array.isArray(doc.wines)
  ) {
    throw new Error('cellar.json is not a cellar document.');
  }
  if (doc.schemaVersion > CELLAR_SCHEMA_VERSION) {
    throw new CellarVersionTooNewError(doc.schemaVersion);
  }
  let cellar = doc as Cellar;
  if (cellar.schemaVersion < 2) cellar = migrateCellarV1ToV2(cellar);
  if (cellar.schemaVersion < 3) cellar = migrateCellarV2ToV3(cellar);
  if (cellar.schemaVersion < 4) cellar = migrateCellarV3ToV4(cellar);
  if (cellar.schemaVersion < 5) cellar = migrateCellarV4ToV5(cellar);
  if (cellar.schemaVersion < 6) cellar = migrateCellarV5ToV6(cellar);
  if (cellar.schemaVersion < 7) cellar = migrateCellarV6ToV7(cellar);
  if (cellar.schemaVersion < 8) cellar = migrateCellarV7ToV8(cellar);
  return cellar;
}
